import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { useAuthStore } from '@/stores/auth-store';

type Theme = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'hancomum-theme';

function applyTheme(theme: Theme) {
  const root = document.documentElement;
  const isDark =
    theme === 'dark' ||
    (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  root.classList.toggle('dark', isDark);
  localStorage.setItem(STORAGE_KEY, theme);
}

/** Apply saved theme immediately (before React renders) to prevent flash */
export function initTheme() {
  const saved = (localStorage.getItem(STORAGE_KEY) as Theme) ?? 'light';
  applyTheme(saved);
}

export function useTheme() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const { data: settings } = useQuery<{ theme: Theme }>({
    queryKey: ['settings'],
    queryFn: async () => {
      const res = await api.get('/settings');
      return res.data.data;
    },
    enabled: isAuthenticated,
    staleTime: 60 * 1000,
  });

  const theme: Theme = settings?.theme ?? (localStorage.getItem(STORAGE_KEY) as Theme) ?? 'light';

  // Apply whenever theme changes
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Listen for system preference changes when in 'system' mode
  useEffect(() => {
    if (theme !== 'system') return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => applyTheme('system');
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [theme]);
}
